/**
 * ApplicationsPanel.jsx · v2.7.65+E77 · Admin Applications console
 * ════════════════════════════════════════════════════════════════
 * Lives under: Admin → TAXI COMMANDER ADMIN → User Management → Applications.
 * Renders the full list of public "Drive With Us" submissions, supports:
 *   · Filter by status/position + free-text search (name/email/phone)
 *   · Detail modal with full applicant record + resume download
 *   · "Copy Onboarding Link" action that puts the private
 *     `/onboarding/contractor-setup` absolute URL on the admin's clipboard
 *   · Status update (Pending / Reviewed / Contacted / Hired / Rejected)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";
import { jsPDF } from "jspdf";
import {
  Loader2, Search, RefreshCw, Copy, FileText, X, CheckCircle2,
  UserCircle2, MapPin, Phone, Mail, Briefcase, Calendar, ClipboardList,
  ShieldCheck, Download, KeyRound, Landmark, Users, PenLine, Sparkles,
  Eye, Printer,
} from "lucide-react";

const STATUSES = ["Pending", "Reviewed", "Contacted", "Hired", "Rejected"];
const STATUS_STYLE = {
  Pending:   "bg-amber-500/15 text-amber-300 border-amber-600/40",
  Reviewed:  "bg-sky-500/15 text-sky-300 border-sky-600/40",
  Contacted: "bg-indigo-500/15 text-indigo-300 border-indigo-600/40",
  Hired:     "bg-emerald-500/15 text-emerald-300 border-emerald-600/40",
  Rejected:  "bg-rose-500/15 text-rose-300 border-rose-600/40",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) +
           " · " + d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

// v2.7.65+E83 · Compact "2h ago" style for the shared-with audit sub-line
function relativeTime(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const now  = Date.now();
    const s = Math.max(0, Math.floor((now - then) / 1000));
    if (s < 60)       return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60)       return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24)       return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30)       return `${d}d ago`;
    const mo = Math.floor(d / 30);
    if (mo < 12)      return `${mo}mo ago`;
    return `${Math.floor(mo / 12)}y ago`;
  } catch { return ""; }
}

// ── v2.7.65+E81 · Full-application compliance print + PDF export ─────────
/**
 * Build a print-ready HTML document string for a single application +
 * (optional) contractor-setup packet. Used by both the iframe print flow
 * (Print button) and the fallback preview if PDF export ever fails.
 */
function _buildPrintHTML(a, setup) {
  const esc = (v) => String(v ?? "—")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const bk = process.env.REACT_APP_BACKEND_URL || "";
  const fullName = [a.first_name, a.middle_name, a.last_name].filter(Boolean).join(" ");
  const submittedOn = a.created_at ? new Date(a.created_at).toLocaleString() : "—";
  const setupSection = setup ? `
    <h2>Onboarding Packet</h2>
    <table>
      <tr><td class="k">DOB</td><td>${esc(setup.date_of_birth)}</td>
          <td class="k">SSN (last 4)</td><td>•••••${esc(setup.ssn_last4)}</td></tr>
      <tr><td class="k">DL #</td><td>${esc(setup.dl_number)}</td>
          <td class="k">DL Exp / State</td><td>${esc(setup.dl_expiration_date)} · ${esc(setup.dl_issuing_state)}</td></tr>
      <tr><td class="k">Bank</td><td>${esc(setup.banking?.bank_name)}</td>
          <td class="k">Account</td><td>${esc(setup.banking?.account_type)} · ${esc(setup.banking?.account_number)}</td></tr>
      <tr><td class="k">Routing</td><td>${esc(setup.banking?.routing_number)}</td>
          <td class="k">TIN (${esc((setup.w9?.tin_type || "").toUpperCase())})</td><td>${esc(setup.w9?.tin_number)}</td></tr>
      <tr><td class="k">W-9 Legal Name</td><td colspan="3">${esc(setup.w9?.legal_name)}</td></tr>
      <tr><td class="k">Emergency Contact</td><td colspan="3">${esc(setup.emergency_contact?.name)} · ${esc(setup.emergency_contact?.phone)} · ${esc(setup.emergency_contact?.relationship)}</td></tr>
      <tr><td class="k">Availability</td><td colspan="3">${esc((setup.availability?.days_available || []).join(", "))} · ${esc(setup.availability?.start_time)}–${esc(setup.availability?.end_time)}</td></tr>
      <tr><td class="k">Background Release</td><td colspan="3">
        Agreed on ${esc(setup.background_release_date)}
        ${setup.signature_pdf_url ? ` · <a href="${bk}${esc(setup.signature_pdf_url)}">Signed release PDF</a>` : ""}
      </td></tr>
    </table>

    <h3>Professional References</h3>
    <ol class="refs">
      ${(setup.professional_references || []).map((r) => `
        <li><b>${esc(r.name)}</b> · ${esc(r.relationship)}<br>
          <span class="muted">${esc(r.phone)} · ${esc(r.email)}</span></li>`).join("")}
    </ol>

    <h3>Uploaded Documents</h3>
    <ul class="docs">
      ${(setup.documents || []).map((d) => `
        <li>${esc(d.slot.replace(/_/g, " ").toUpperCase())}${d.doc_type ? ` · ${esc(d.doc_type)}` : ""} — <span class="muted">${esc(d.filename)}</span></li>`).join("")}
    </ul>` : `
    <h2>Onboarding Packet</h2>
    <p class="muted">Not yet submitted. Copy the private onboarding link to send to the candidate.</p>`;

  return `<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Application · ${esc(fullName)}</title>
    <style>
      *,*::before,*::after { box-sizing: border-box; }
      body { font-family: -apple-system, Segoe UI, Roboto, Arial, sans-serif; color: #222; margin: 32px; }
      h1 { margin: 0 0 4px; font-size: 22px; }
      h2 { margin: 22px 0 8px; font-size: 15px; text-transform: uppercase; letter-spacing: 1px; border-bottom: 1px solid #ddd; padding-bottom: 3px; color: #b6841a; }
      h3 { margin: 14px 0 6px; font-size: 12px; text-transform: uppercase; letter-spacing: 1px; color: #555; }
      .sub { color: #666; font-size: 11px; }
      table { width: 100%; border-collapse: collapse; font-size: 12px; }
      td { padding: 5px 8px; border-bottom: 1px solid #eee; vertical-align: top; }
      td.k { color: #666; width: 22%; font-weight: bold; }
      .muted { color: #666; font-size: 11px; }
      .status { display: inline-block; padding: 2px 8px; border-radius: 3px; font-size: 10px; font-weight: bold; text-transform: uppercase; background: #eee; color: #333; }
      .status-Hired { background: #d1fae5; color: #065f46; }
      .status-Contacted { background: #e0e7ff; color: #3730a3; }
      .status-Rejected { background: #fee2e2; color: #991b1b; }
      .status-Reviewed { background: #dbeafe; color: #1e40af; }
      .status-Pending { background: #fef3c7; color: #92400e; }
      ol.refs, ul.docs { padding-left: 18px; font-size: 12px; }
      ol.refs li, ul.docs li { margin: 4px 0; }
      a { color: #b6841a; text-decoration: none; }
      .head { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px; }
      .head .brand { text-align: right; color: #666; font-size: 10px; }
      @media print {
        body { margin: 0; padding: 24px; }
        h2 { break-after: avoid; }
        table, ol, ul { break-inside: avoid; }
      }
    </style></head>
    <body>
      <div class="head">
        <div>
          <h1>${esc(fullName)}</h1>
          <div class="sub">
            <span class="status status-${esc(a.status)}">${esc(a.status)}</span>
            &nbsp;·&nbsp; Submitted ${esc(submittedOn)}
          </div>
        </div>
        <div class="brand">
          Buller Taxi Services<br>
          Hiring Application · Compliance Print<br>
          Generated ${new Date().toLocaleString()}
        </div>
      </div>

      <h2>Applicant Details</h2>
      <table>
        <tr><td class="k">Position</td><td colspan="3"><b>${esc(a.position_applied)}</b></td></tr>
        <tr><td class="k">Email</td><td>${esc(a.email)}</td>
            <td class="k">Cell Phone</td><td>${esc(a.cell_phone)}</td></tr>
        <tr><td class="k">Home Phone</td><td>${esc(a.home_phone || "—")}</td>
            <td class="k">Application ID</td><td class="muted" style="font-family:monospace">${esc(a.id)}</td></tr>
        <tr><td class="k">Address</td><td colspan="3">
          ${esc(a.address)}<br>${esc(a.city)}, ${esc(a.state)} ${esc(a.zip)}
        </td></tr>
        <tr><td class="k">Resume</td><td colspan="3">
          ${a.resume_url ? `<a href="${bk}${esc(a.resume_url)}">${esc(a.resume_filename || "Download resume")}</a>` : `<span class="muted">Not attached</span>`}
        </td></tr>
        ${a.last_note ? `<tr><td class="k">Last Note</td><td colspan="3" class="muted" style="white-space: pre-wrap">${esc(a.last_note)}</td></tr>` : ""}
      </table>

      ${setupSection}
    </body></html>`;
}

/** Iframe-print flow (uses the browser's native print dialog — user can
 *  also choose "Save as PDF" from that dialog). */
function printFullApplication(a, setup) {
  const html = _buildPrintHTML(a, setup);
  const prior = document.getElementById("app-fullprint-frame");
  if (prior) prior.remove();
  const iframe = document.createElement("iframe");
  iframe.id = "app-fullprint-frame";
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
  document.body.appendChild(iframe);
  const doc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!doc) return;
  doc.open();
  doc.write(html);
  doc.close();
  // Wait a tick so styles apply, then print.
  setTimeout(() => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } catch { /* soft-fail */ }
  }, 300);
}

/** Direct PDF export via jsPDF — bypasses the print dialog entirely. */
function exportFullApplicationPDF(a, setup) {
  const pdf = _buildFullApplicationPDF(a, setup);
  const fullName = [a.first_name, a.middle_name, a.last_name].filter(Boolean).join(" ");
  const safeName = (fullName || "application").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  pdf.save(`buller-application-${safeName}.pdf`);
}

/** Same PDF assembly but returns the Blob for network transmission (email). */
function buildApplicationPDFBlob(a, setup) {
  const pdf = _buildFullApplicationPDF(a, setup);
  return pdf.output("blob");
}

/** Shared jsPDF assembly — used by both `exportFullApplicationPDF` (direct
 *  save) and `buildApplicationPDFBlob` (email attachment). */
function _buildFullApplicationPDF(a, setup) {
  const pdf = new jsPDF({ unit: "pt", format: "letter" });
  const M = 48;                 // page margin
  const W = 612 - M * 2;        // usable width
  const H = 792;
  let y = M;

  const fullName = [a.first_name, a.middle_name, a.last_name].filter(Boolean).join(" ");
  const line = (txt, opts = {}) => {
    const { size = 11, bold = false, color = 20, indent = 0, gap = 6 } = opts;
    pdf.setFont("helvetica", bold ? "bold" : "normal");
    pdf.setFontSize(size);
    pdf.setTextColor(color);
    const wrapped = pdf.splitTextToSize(txt, W - indent);
    // Page break check.
    if (y + wrapped.length * (size + 2) > H - M) {
      pdf.addPage();
      y = M;
    }
    pdf.text(wrapped, M + indent, y);
    y += wrapped.length * (size + 2) + gap;
  };
  const kv = (k, v) => {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.setTextColor(110);
    pdf.text(String(k), M, y);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(20);
    const val = pdf.splitTextToSize(String(v ?? "—"), W - 130);
    if (y + val.length * 12 > H - M) { pdf.addPage(); y = M; }
    pdf.text(val, M + 130, y);
    y += val.length * 12 + 3;
  };
  const heading = (t) => {
    y += 6;
    if (y > H - M - 30) { pdf.addPage(); y = M; }
    pdf.setDrawColor(210);
    pdf.setFillColor(182, 132, 26);
    pdf.rect(M, y - 10, 4, 12, "F");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);
    pdf.setTextColor(182, 132, 26);
    pdf.text(t.toUpperCase(), M + 10, y);
    y += 6;
    pdf.setDrawColor(220);
    pdf.line(M, y, M + W, y);
    y += 14;
  };

  // Header
  pdf.setFont("helvetica", "bold"); pdf.setFontSize(18); pdf.setTextColor(20);
  pdf.text(fullName || "Application", M, y); y += 22;
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(9); pdf.setTextColor(110);
  pdf.text(`Status: ${a.status}   ·   Submitted ${a.created_at ? new Date(a.created_at).toLocaleString() : "—"}`, M, y);
  y += 12;
  pdf.text("Buller Taxi Services · Hiring Application · Compliance Print", M, y);
  y += 10;
  pdf.text(`Generated ${new Date().toLocaleString()}`, M, y);
  y += 18;

  heading("Applicant Details");
  kv("Position", a.position_applied);
  kv("Email", a.email);
  kv("Cell Phone", a.cell_phone);
  kv("Home Phone", a.home_phone || "—");
  kv("Address", `${a.address || ""}, ${a.city || ""}, ${a.state || ""} ${a.zip || ""}`);
  kv("Application ID", a.id);
  if (a.resume_url) {
    kv("Resume", `${a.resume_filename || "Download resume"}\n${process.env.REACT_APP_BACKEND_URL}${a.resume_url}`);
  } else {
    kv("Resume", "Not attached");
  }
  if (a.last_note) {
    y += 4;
    line("Last Note:", { size: 10, bold: true, color: 90 });
    line(a.last_note, { size: 10, color: 60 });
  }

  if (setup) {
    heading("Onboarding Packet");
    kv("Date of Birth", setup.date_of_birth);
    kv("SSN (last 4)", "•••••" + (setup.ssn_last4 || "—"));
    kv("DL Number", setup.dl_number);
    kv("DL Expiration", `${setup.dl_expiration_date} · ${setup.dl_issuing_state}`);

    heading("Banking (masked)");
    kv("Bank", setup.banking?.bank_name);
    kv("Account Type", setup.banking?.account_type);
    kv("Routing #", setup.banking?.routing_number);
    kv("Account #", setup.banking?.account_number);

    heading("W-9 Tax Information");
    kv("Legal Name", setup.w9?.legal_name);
    if (setup.w9?.business_name) kv("Business Name", setup.w9.business_name);
    kv("Tax Classification", (setup.w9?.tax_classification || "").replace(/_/g, " "));
    kv(`${(setup.w9?.tin_type || "TIN").toUpperCase()} #`, setup.w9?.tin_number);

    heading("Emergency Contact");
    kv("Name", setup.emergency_contact?.name);
    kv("Phone", setup.emergency_contact?.phone);
    kv("Relationship", setup.emergency_contact?.relationship);

    heading("Availability");
    kv("Days", (setup.availability?.days_available || []).join(", "));
    kv("Hours", `${setup.availability?.start_time || "—"} to ${setup.availability?.end_time || "—"}`);

    heading("Professional References");
    (setup.professional_references || []).forEach((r, i) => {
      line(`${i + 1}. ${r.name}  ·  ${r.relationship}`, { size: 11, bold: true, gap: 2 });
      line(`   ${r.phone}  ·  ${r.email}`, { size: 10, color: 90, gap: 6 });
    });

    heading("Uploaded Documents");
    (setup.documents || []).forEach((d) => {
      const label = d.slot.replace(/_/g, " ").toUpperCase();
      line(`• ${label}${d.doc_type ? ` — ${d.doc_type}` : ""}: ${d.filename}`, { size: 10, gap: 3 });
    });

    heading("Background Release");
    kv("Agreed On", setup.background_release_date);
    if (setup.signature_pdf_url) {
      kv("Signed PDF", `${process.env.REACT_APP_BACKEND_URL}${setup.signature_pdf_url}`);
    }
  } else {
    heading("Onboarding Packet");
    line("Not yet submitted. Copy the private onboarding link to send to the candidate.",
         { size: 10, color: 90 });
  }

  // Footer on last page
  pdf.setFont("helvetica", "italic"); pdf.setFontSize(8); pdf.setTextColor(150);
  pdf.text(`Application ID: ${a.id}`, M, H - 24);
  pdf.text("This document is confidential and intended for authorized personnel only.", M, H - 12);

  return pdf;
}

export default function ApplicationsPanel() {
  const [items, setItems] = useState([]);
  const [counters, setCounters] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [savingStatus, setSavingStatus] = useState(false);
  // v2.7.65+E136 · Bulk multi-select for Friday-review sessions.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (statusFilter !== "all") params.status = statusFilter;
      if (q.trim())               params.q      = q.trim();
      const { data } = await adminApi.get(`/admin/applications`, { params });
      setItems(data.items || []);
      setCounters(data.counters || {});
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to load applications";
      toast.error(typeof msg === "string" ? msg : "Failed to load applications");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, q]);

  useEffect(() => { load(); }, [load]);

  const copyOnboardingLink = useCallback(async () => {
    const url = `${window.location.origin}/onboarding/contractor-setup`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Copied to clipboard!");
    } catch {
      // clipboard blocked (private mode/iframe) — fall back to a prompt
      try {
        const ta = document.createElement("textarea");
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success("Copied to clipboard!");
      } catch {
        toast.error("Could not copy — please copy manually: " + url);
      }
    }
  }, []);

  const updateStatus = useCallback(async (id, newStatus) => {
    setSavingStatus(true);
    try {
      const { data } = await adminApi.patch(
        `/admin/applications/${id}/status`,
        { status: newStatus },
      );
      toast.success(`Status → ${newStatus}`);
      setItems((prev) => prev.map((r) => (r.id === id ? data.application : r)));
      if (detail?.id === id) setDetail(data.application);
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Update failed";
      toast.error(typeof msg === "string" ? msg : "Update failed");
    } finally {
      setSavingStatus(false);
    }
  }, [detail, load]);

  // v2.7.65+E136 · Bulk multi-select helpers
  const toggleRow = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAllVisible = useCallback(() => {
    setSelectedIds((prev) => {
      const visibleIds = items.map((r) => r.id);
      const allSelected = visibleIds.every((id) => prev.has(id));
      if (allSelected) {
        // Deselect all visible rows only (keep off-page selections intact).
        const next = new Set(prev);
        visibleIds.forEach((id) => next.delete(id));
        return next;
      }
      return new Set([...prev, ...visibleIds]);
    });
  }, [items]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const bulkApplyStatus = useCallback(async (targetStatus) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const label = targetStatus === "Rejected" ? "reject" : `mark ${targetStatus.toLowerCase()}`;
    if (!window.confirm(`${ids.length === 1 ? "This will" : `These will`} ${label} ${ids.length} application${ids.length === 1 ? "" : "s"}. Continue?`)) return;
    setBulkBusy(true);
    try {
      const { data } = await adminApi.post("/admin/applications/bulk-status", {
        application_ids: ids,
        status:          targetStatus,
      });
      toast.success(`Updated ${data.modified}/${data.requested} → ${targetStatus}`);
      clearSelection();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk update failed");
    } finally {
      setBulkBusy(false);
    }
  }, [selectedIds, load, clearSelection]);

  const kpi = useMemo(() => ([
    { key: "total",     label: "Total",     tone: "text-zinc-100" },
    { key: "Pending",   label: "Pending",   tone: "text-amber-300" },
    { key: "Reviewed",  label: "Reviewed",  tone: "text-sky-300" },
    { key: "Contacted", label: "Contacted", tone: "text-indigo-300" },
    { key: "Hired",     label: "Hired",     tone: "text-emerald-300" },
    { key: "Rejected",  label: "Rejected",  tone: "text-rose-300" },
  ]), []);

  return (
    <div className="space-y-4" data-testid="applications-panel">
      {/* Header + copy-onboarding-link CTA */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-black tracking-tight text-zinc-100">Applications</h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Public submissions from <span className="text-amber-300">/drive-with-us</span>.
            Click a row to review and send the private onboarding link.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={copyOnboardingLink}
            data-testid="copy-onboarding-link-top"
            className="inline-flex items-center gap-2 bg-amber-400 text-zinc-950 hover:bg-amber-300 font-bold text-xs uppercase tracking-widest px-4 py-2 rounded transition"
          >
            <Copy className="h-3.5 w-3.5"/>
            Copy Onboarding Link
          </button>
          <button
            type="button"
            onClick={load}
            data-testid="applications-refresh"
            className="inline-flex items-center gap-1.5 bg-zinc-900 border border-zinc-700 hover:border-amber-400/60 text-zinc-200 text-xs px-3 py-2 rounded transition"
            disabled={loading}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`}/>
            Refresh
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        {kpi.map((k) => (
          <button
            key={k.key}
            type="button"
            onClick={() => setStatusFilter(k.key === "total" ? "all" : k.key)}
            data-testid={`applications-kpi-${k.key.toLowerCase()}`}
            className={`text-left rounded border p-2.5 transition ${
              (k.key === "total" && statusFilter === "all") || statusFilter === k.key
                ? "bg-zinc-800 border-amber-400/60"
                : "bg-zinc-950 border-zinc-800 hover:border-zinc-600"
            }`}
          >
            <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">{k.label}</div>
            <div className={`text-xl font-black tabular-nums mt-0.5 ${k.tone}`}>
              {counters[k.key] ?? 0}
            </div>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="h-4 w-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2"/>
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="applications-search"
          placeholder="Search by name, email, or phone…"
          className="w-full bg-zinc-950 border border-zinc-800 focus:border-amber-400 rounded pl-9 pr-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 outline-none transition"
        />
      </div>

      {/* v2.7.65+E136 · Bulk action bar — visible only when at least one
          row is selected. Sticks near the top so a reviewer can mark
          multiple applications in a single click. */}
      {selectedIds.size > 0 && (
        <div
          className="flex flex-wrap items-center gap-2 rounded-lg border-2 border-amber-400/70 bg-amber-500/10 px-3 py-2"
          data-testid="applications-bulk-bar"
        >
          <span className="text-sm font-bold text-amber-200" data-testid="applications-bulk-count">
            {selectedIds.size} selected
          </span>
          <div className="flex-1"/>
          <button
            type="button"
            onClick={() => bulkApplyStatus("Reviewed")}
            disabled={bulkBusy}
            data-testid="bulk-mark-reviewed"
            className="text-[10px] uppercase tracking-widest font-black px-3 py-1.5 rounded bg-sky-500/20 border border-sky-400/40 text-sky-200 hover:bg-sky-500/30 disabled:opacity-50"
          >Mark Reviewed</button>
          <button
            type="button"
            onClick={() => bulkApplyStatus("Contacted")}
            disabled={bulkBusy}
            data-testid="bulk-mark-contacted"
            className="text-[10px] uppercase tracking-widest font-black px-3 py-1.5 rounded bg-indigo-500/20 border border-indigo-400/40 text-indigo-200 hover:bg-indigo-500/30 disabled:opacity-50"
          >Mark Contacted</button>
          <button
            type="button"
            onClick={() => bulkApplyStatus("Hired")}
            disabled={bulkBusy}
            data-testid="bulk-mark-hired"
            className="text-[10px] uppercase tracking-widest font-black px-3 py-1.5 rounded bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/30 disabled:opacity-50"
          >Mark Hired</button>
          <button
            type="button"
            onClick={() => bulkApplyStatus("Rejected")}
            disabled={bulkBusy}
            data-testid="bulk-mark-rejected"
            className="text-[10px] uppercase tracking-widest font-black px-3 py-1.5 rounded bg-rose-500/20 border border-rose-400/40 text-rose-200 hover:bg-rose-500/30 disabled:opacity-50"
          >Reject</button>
          <button
            type="button"
            onClick={clearSelection}
            data-testid="bulk-clear"
            className="text-[10px] uppercase tracking-widest font-bold px-2 py-1.5 rounded text-zinc-400 hover:text-zinc-200"
          >Clear</button>
        </div>
      )}

      {/* Table */}
      <div className="border border-zinc-800 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900 text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
              <tr>
                <th className="px-3 py-2.5 text-center w-8">
                  {/* v2.7.65+E136 · Select-all-visible checkbox */}
                  <input
                    type="checkbox"
                    aria-label="Select all applications on this page"
                    data-testid="applications-select-all"
                    onChange={toggleAllVisible}
                    checked={items.length > 0 && items.every((r) => selectedIds.has(r.id))}
                    className="cursor-pointer accent-amber-400"
                  />
                </th>
                <th className="px-3 py-2.5 text-left">Date</th>
                <th className="px-3 py-2.5 text-left">Name</th>
                <th className="px-3 py-2.5 text-left">Position</th>
                <th className="px-3 py-2.5 text-left">Cell Phone</th>
                <th className="px-3 py-2.5 text-left">Status</th>
                <th className="px-3 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {loading ? (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-zinc-500">
                  <Loader2 className="h-5 w-5 animate-spin inline-block mr-2"/> Loading…
                </td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-zinc-500 text-xs" data-testid="applications-empty">
                  No applications match the current filters.
                </td></tr>
              ) : items.map((r) => (
                <tr
                  key={r.id}
                  className={`hover:bg-zinc-900/50 cursor-pointer transition ${selectedIds.has(r.id) ? "bg-amber-500/5" : ""}`}
                  onClick={() => setDetail(r)}
                  data-testid={`app-row-${r.id}`}
                >
                  {/* v2.7.65+E136 · Per-row select checkbox. Stop the click
                      from bubbling to the row so ticking doesn't also open
                      the detail modal. */}
                  <td className="px-3 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={`Select application from ${r.first_name} ${r.last_name}`}
                      checked={selectedIds.has(r.id)}
                      onChange={() => toggleRow(r.id)}
                      data-testid={`app-select-${r.id}`}
                      className="cursor-pointer accent-amber-400"
                    />
                  </td>
                  <td className="px-3 py-2.5 text-zinc-400 text-xs whitespace-nowrap">{fmtDate(r.created_at)}</td>
                  <td className="px-3 py-2.5 text-zinc-100 font-semibold">
                    <div className="flex items-center gap-1.5">
                      <span>{r.first_name} {r.middle_name ? r.middle_name + " " : ""}{r.last_name}</span>
                      {r.contractor_setup_id && (
                        <span
                          className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-black px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-600/50"
                          data-testid={`app-setup-badge-${r.id}`}
                          title="Contractor onboarding packet received"
                        >
                          <ShieldCheck className="h-2.5 w-2.5"/>
                          Setup
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-zinc-500">{r.email}</div>
                    {/* v2.7.65+E83 · Shared-with audit sub-line */}
                    {r.last_shared_to && (
                      <div
                        className="text-[10px] text-sky-300/80 mt-0.5 inline-flex items-center gap-1"
                        data-testid={`app-shared-${r.id}`}
                        title={`Shared by ${r.last_shared_by || "admin"} on ${r.last_shared_at || ""}`}
                      >
                        <Mail className="h-2.5 w-2.5"/>
                        Shared to {r.last_shared_to} · {relativeTime(r.last_shared_at)}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-zinc-300 text-xs">{r.position_applied}</td>
                  <td className="px-3 py-2.5 text-zinc-300 text-xs tabular-nums">{r.cell_phone}</td>
                  <td className="px-3 py-2.5">
                    <span
                      className={`inline-flex text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full border ${STATUS_STYLE[r.status] || STATUS_STYLE.Pending}`}
                      data-testid={`app-status-${r.id}`}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setDetail(r); }}
                      data-testid={`app-view-${r.id}`}
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-amber-300 hover:text-amber-200"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail modal */}
      {detail && (
        <ApplicationDetailModal
          application={detail}
          onClose={() => setDetail(null)}
          onCopyLink={copyOnboardingLink}
          onStatusChange={(newStatus) => updateStatus(detail.id, newStatus)}
          saving={savingStatus}
        />
      )}
    </div>
  );
}

function ApplicationDetailModal({ application: a, onClose, onCopyLink, onStatusChange, saving }) {
  const [activeTab, setActiveTab] = useState("application");
  const [setup, setSetup] = useState(null);
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupError, setSetupError] = useState("");
  const resumeHref = a.resume_url
    ? `${process.env.REACT_APP_BACKEND_URL}${a.resume_url}`
    : null;
  const hasSetup = !!a.contractor_setup_id;

  // Lazy-load the contractor setup only when the tab is opened.
  useEffect(() => {
    if (activeTab !== "setup" || setup || !hasSetup) return;
    let cancelled = false;
    (async () => {
      setSetupLoading(true);
      setSetupError("");
      try {
        const { data } = await adminApi.get(`/contractor-setup/admin/by-application/${a.id}`);
        if (!cancelled) setSetup(data);
      } catch (err) {
        if (cancelled) return;
        const msg = err?.response?.data?.detail || "Failed to load onboarding packet";
        setSetupError(typeof msg === "string" ? msg : "Failed to load onboarding packet");
      } finally {
        if (!cancelled) setSetupLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, hasSetup, a.id, setup]);

  // v2.7.65+E81 · Ensure the onboarding setup is loaded before printing/
  // exporting, so the Print/PDF output includes the packet even when the
  // Onboarding tab was never opened.
  const [printBusy, setPrintBusy] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailForm, setEmailForm] = useState({ recipient: "", note: "" });
  const _ensureSetup = useCallback(async () => {
    if (!hasSetup || setup) return setup;
    try {
      const { data } = await adminApi.get(`/contractor-setup/admin/by-application/${a.id}`);
      setSetup(data);
      return data;
    } catch {
      // If fetch fails we still print — the doc just omits the packet.
      return null;
    }
  }, [hasSetup, setup, a.id]);
  const handlePrint = useCallback(async () => {
    setPrintBusy(true);
    try {
      const packet = await _ensureSetup();
      printFullApplication(a, packet);
    } finally { setPrintBusy(false); }
  }, [a, _ensureSetup]);
  const handleExportPDF = useCallback(async () => {
    setPrintBusy(true);
    try {
      const packet = await _ensureSetup();
      exportFullApplicationPDF(a, packet);
      toast.success("PDF downloaded");
    } catch (err) {
      toast.error("PDF export failed");
    } finally { setPrintBusy(false); }
  }, [a, _ensureSetup]);

  // v2.7.65+E82 · Compile the PDF client-side and post it to the backend
  // as a multipart attachment. Server hands it off to Resend, which
  // emails it to the specified recipient.
  const handleSendEmail = useCallback(async () => {
    const recipient = emailForm.recipient.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(recipient)) {
      toast.error("Enter a valid recipient email");
      return;
    }
    setEmailBusy(true);
    try {
      const packet = await _ensureSetup();
      const blob = buildApplicationPDFBlob(a, packet);
      const fullName = [a.first_name, a.middle_name, a.last_name].filter(Boolean).join(" ");
      const safeName = (fullName || "application").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
      const fd = new FormData();
      fd.append("pdf", blob, `buller-application-${safeName}.pdf`);
      fd.append("recipient_email", recipient);
      if (emailForm.note.trim()) fd.append("note", emailForm.note.trim());
      await adminApi.post(`/admin/applications/${a.id}/email-pdf`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Sent to ${recipient}`);
      setEmailOpen(false);
      setEmailForm({ recipient: "", note: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Send failed";
      toast.error(typeof msg === "string" ? msg : "Send failed");
    } finally { setEmailBusy(false); }
  }, [a, _ensureSetup, emailForm]);

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4 py-6 overflow-y-auto"
      onClick={onClose}
      data-testid="application-detail-modal"
    >
      <div
        className="bg-zinc-950 border border-zinc-700 rounded-lg max-w-3xl w-full my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-zinc-800">
          <div className="flex items-start gap-3">
            <UserCircle2 className="h-9 w-9 text-amber-400 shrink-0"/>
            <div>
              <div className="text-lg font-black text-zinc-100">
                {a.first_name} {a.middle_name || ""} {a.last_name}
              </div>
              <div className="text-xs text-zinc-500 mt-0.5">
                Submitted {fmtDate(a.created_at)}
              </div>
              <div className="flex items-center gap-1.5 mt-1.5">
                <span
                  className={`inline-flex text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full border ${STATUS_STYLE[a.status] || STATUS_STYLE.Pending}`}
                >
                  {a.status}
                </span>
                {hasSetup && (
                  <span
                    className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-black px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-600/50"
                    data-testid="app-modal-setup-badge"
                  >
                    <ShieldCheck className="h-2.5 w-2.5"/>
                    Onboarding packet received
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-start gap-2">
            {/* v2.7.65+E81 · Compliance print + PDF export */}
            <button
              type="button"
              onClick={handlePrint}
              disabled={printBusy}
              data-testid="app-print-full"
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-zinc-300 hover:text-amber-300 border border-zinc-700 hover:border-amber-400/60 rounded px-2.5 py-1.5 bg-zinc-900 transition disabled:opacity-50"
              title="Print the full application on one page"
            >
              {printBusy ? <Loader2 className="h-3 w-3 animate-spin"/> : <Printer className="h-3 w-3"/>}
              Print
            </button>
            <button
              type="button"
              onClick={handleExportPDF}
              disabled={printBusy}
              data-testid="app-export-pdf"
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-zinc-300 hover:text-amber-300 border border-zinc-700 hover:border-amber-400/60 rounded px-2.5 py-1.5 bg-zinc-900 transition disabled:opacity-50"
              title="Download a PDF of this application"
            >
              {printBusy ? <Loader2 className="h-3 w-3 animate-spin"/> : <Download className="h-3 w-3"/>}
              Export PDF
            </button>
            {/* v2.7.65+E82 · Share the PDF by email */}
            <button
              type="button"
              onClick={() => setEmailOpen(true)}
              disabled={printBusy}
              data-testid="app-email-pdf"
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-zinc-300 hover:text-amber-300 border border-zinc-700 hover:border-amber-400/60 rounded px-2.5 py-1.5 bg-zinc-900 transition disabled:opacity-50"
              title="Send this application PDF to a recipient"
            >
              <Mail className="h-3 w-3"/> Send by Email
            </button>
            <button
              type="button"
              onClick={onClose}
              data-testid="application-detail-close"
              className="text-zinc-400 hover:text-zinc-100 ml-1"
            >
              <X className="h-5 w-5"/>
            </button>
          </div>
        </div>

        {/* Tab switcher */}
        <div className="flex border-b border-zinc-800 bg-zinc-950/50" role="tablist">
          <TabBtn active={activeTab === "application"} onClick={() => setActiveTab("application")} testid="app-tab-application">
            <FileText className="h-3.5 w-3.5"/>
            Application
          </TabBtn>
          <TabBtn active={activeTab === "setup"} onClick={() => setActiveTab("setup")} testid="app-tab-setup" disabled={!hasSetup}>
            <ShieldCheck className="h-3.5 w-3.5"/>
            Onboarding Packet
            {hasSetup && <span className="ml-1 text-[8px] text-emerald-400">●</span>}
          </TabBtn>
        </div>

        {/* Body */}
        {activeTab === "application" ? (
          <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
            <Section icon={Briefcase} title="Applying For">
              <div className="text-sm text-zinc-100 font-bold">{a.position_applied}</div>
            </Section>

            <Section icon={Mail} title="Contact">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <div><span className="text-zinc-500">Email:</span> <a href={`mailto:${a.email}`} className="text-amber-300 hover:text-amber-200">{a.email}</a></div>
                <div><span className="text-zinc-500">Cell:</span> <a href={`tel:${a.cell_phone}`} className="text-zinc-100">{a.cell_phone}</a></div>
                {a.home_phone && (
                  <div><span className="text-zinc-500">Home:</span> <span className="text-zinc-300">{a.home_phone}</span></div>
                )}
              </div>
            </Section>

            <Section icon={MapPin} title="Address">
              <div className="text-sm text-zinc-200">
                {a.address}<br/>
                {a.city}, {a.state} {a.zip}
              </div>
            </Section>

            <Section icon={FileText} title="Resume">
              {resumeHref ? (
                <FileActions
                  url={resumeHref}
                  filename={a.resume_filename || "resume"}
                  testidPrefix="application-resume"
                />
              ) : (
                <div className="text-xs text-zinc-500 italic">No resume attached.</div>
              )}
            </Section>

            {a.last_note && (
              <Section icon={ClipboardList} title="Last Note">
                <div className="text-xs text-zinc-300 leading-relaxed whitespace-pre-wrap">{a.last_note}</div>
              </Section>
            )}
          </div>
        ) : (
          <div className="px-6 py-4 max-h-[60vh] overflow-y-auto" data-testid="app-setup-tab-body">
            {!hasSetup ? (
              <div className="text-center py-10 text-zinc-500 text-xs">
                <ShieldCheck className="h-8 w-8 mx-auto mb-2 opacity-40"/>
                Candidate has not yet completed the onboarding packet.
                Use &quot;Copy Onboarding Link&quot; below to send them the private setup URL.
              </div>
            ) : setupLoading ? (
              <div className="text-center py-10 text-zinc-500 text-xs">
                <Loader2 className="h-6 w-6 mx-auto animate-spin mb-2"/>
                Loading onboarding packet…
              </div>
            ) : setupError ? (
              <div className="text-center py-10 text-rose-400 text-xs" data-testid="app-setup-error">
                {setupError}
              </div>
            ) : setup ? (
              <ContractorSetupView setup={setup}/>
            ) : null}
          </div>
        )}

        {/* Footer actions */}
        <div className="px-6 py-4 border-t border-zinc-800 space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1.5">Change status</div>
            <div className="flex flex-wrap gap-1.5">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => onStatusChange(s)}
                  disabled={saving || a.status === s}
                  data-testid={`app-set-status-${s.toLowerCase()}`}
                  className={`text-[10px] uppercase tracking-widest font-black px-3 py-1.5 rounded-full border transition ${
                    a.status === s
                      ? "bg-amber-400 text-zinc-950 border-amber-400 cursor-default"
                      : "bg-zinc-900 text-zinc-300 border-zinc-700 hover:border-amber-400/60"
                  } disabled:opacity-50`}
                >
                  {a.status === s && <CheckCircle2 className="h-3 w-3 inline mr-1"/>}
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <button
              type="button"
              onClick={onCopyLink}
              data-testid="app-detail-copy-onboarding-link"
              className="inline-flex items-center justify-center gap-2 bg-amber-400 text-zinc-950 hover:bg-amber-300 font-bold text-xs uppercase tracking-widest px-4 py-2.5 rounded flex-1"
            >
              <Copy className="h-3.5 w-3.5"/>
              Copy Onboarding Link
            </button>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center gap-2 bg-zinc-900 border border-zinc-700 hover:border-zinc-600 text-zinc-200 text-xs px-4 py-2.5 rounded"
            >
              Close
            </button>
          </div>
        </div>

        {/* v2.7.65+E82 · Email dialog (inline overlay) */}
        {emailOpen && (
          <div
            className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4"
            onClick={() => !emailBusy && setEmailOpen(false)}
            data-testid="app-email-dialog"
          >
            <div
              className="bg-zinc-950 border border-zinc-700 rounded-lg max-w-md w-full p-6 space-y-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start gap-2">
                <Mail className="h-5 w-5 text-amber-400 shrink-0 mt-0.5"/>
                <div>
                  <h3 className="text-base font-black text-zinc-100">Send Application by Email</h3>
                  <p className="text-[11px] text-zinc-500 mt-0.5">
                    Compiles the full PDF (including onboarding packet, if received) and emails it as an attachment.
                  </p>
                </div>
              </div>
              <label className="block">
                <span className="block text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1">Recipient Email *</span>
                <input
                  type="email"
                  autoFocus
                  value={emailForm.recipient}
                  onChange={(e) => setEmailForm((f) => ({ ...f, recipient: e.target.value }))}
                  disabled={emailBusy}
                  data-testid="app-email-recipient"
                  className="w-full bg-zinc-900 border border-zinc-700 focus:border-amber-400 rounded px-3 py-2 text-sm text-zinc-100 outline-none"
                  placeholder="partner@example.com"
                />
              </label>
              <label className="block">
                <span className="block text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1">Optional Note</span>
                <textarea
                  rows={3}
                  value={emailForm.note}
                  onChange={(e) => setEmailForm((f) => ({ ...f, note: e.target.value }))}
                  disabled={emailBusy}
                  data-testid="app-email-note"
                  className="w-full bg-zinc-900 border border-zinc-700 focus:border-amber-400 rounded px-3 py-2 text-sm text-zinc-100 outline-none resize-none"
                  placeholder="e.g. Please review — this is our top candidate for the dispatch role."
                />
              </label>
              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setEmailOpen(false)}
                  disabled={emailBusy}
                  className="text-xs text-zinc-400 hover:text-zinc-200 px-3 py-2"
                  data-testid="app-email-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSendEmail}
                  disabled={emailBusy}
                  data-testid="app-email-send"
                  className="inline-flex items-center gap-1.5 bg-amber-400 hover:bg-amber-300 disabled:opacity-50 text-zinc-950 font-bold text-xs uppercase tracking-widest px-4 py-2 rounded"
                >
                  {emailBusy && <Loader2 className="h-3.5 w-3.5 animate-spin"/>}
                  {emailBusy ? "Sending…" : "Send"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, disabled, testid, children }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      disabled={disabled}
      onClick={onClick}
      data-testid={testid}
      className={`flex-1 inline-flex items-center justify-center gap-1.5 text-[11px] uppercase tracking-widest font-black px-4 py-2.5 border-b-2 transition disabled:opacity-40 disabled:cursor-not-allowed ${
        active
          ? "text-amber-300 border-amber-400 bg-zinc-900/40"
          : "text-zinc-500 border-transparent hover:text-zinc-300"
      }`}
    >
      {children}
    </button>
  );
}

function ContractorSetupView({ setup }) {
  const bk = `${process.env.REACT_APP_BACKEND_URL}`;
  const docs = setup.documents || [];
  const doc = (slot) => docs.find((d) => d.slot === slot);
  return (
    <div className="space-y-4">
      {/* Identity summary */}
      <Section icon={KeyRound} title="Identity + SSN">
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
          <div><span className="text-zinc-500">DOB:</span> <span className="text-zinc-200">{setup.date_of_birth}</span></div>
          <div><span className="text-zinc-500">SSN:</span> <span className="text-zinc-200 font-mono">•••••{setup.ssn_last4 || "—"}</span></div>
        </div>
      </Section>

      {/* DL */}
      <Section icon={FileText} title="Driver's License">
        <div className="grid grid-cols-3 gap-x-4 gap-y-1.5 text-xs">
          <div><span className="text-zinc-500">#</span> <span className="text-zinc-200 font-mono">{setup.dl_number}</span></div>
          <div><span className="text-zinc-500">Exp:</span> <span className="text-zinc-200">{setup.dl_expiration_date}</span></div>
          <div><span className="text-zinc-500">State:</span> <span className="text-zinc-200">{setup.dl_issuing_state}</span></div>
        </div>
      </Section>

      {/* Documents */}
      <Section icon={FileText} title="Documents (4 slots)">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {[
            { slot: "dl_front", label: "Front of DL" },
            { slot: "dl_back",  label: "Back of DL"  },
            { slot: "insurance", label: "Insurance" },
            { slot: "slot4",     label: "Supporting Doc" },
          ].map(({ slot, label }) => {
            const d = doc(slot);
            return (
              <div key={slot} className="border border-zinc-800 rounded p-2.5 text-xs space-y-2">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                  {label}{d?.doc_type ? ` · ${d.doc_type}` : ""}
                </div>
                {d ? (
                  <FileActions
                    url={`${bk}${d.url}`}
                    filename={d.filename || `${slot}.bin`}
                    testidPrefix={`setup-doc-${slot}`}
                  />
                ) : (
                  <div className="text-zinc-600 italic">Not provided</div>
                )}
              </div>
            );
          })}
        </div>
      </Section>

      {/* Address */}
      <Section icon={MapPin} title="Address">
        <div className="text-xs text-zinc-200">
          {setup.address}<br/>
          {setup.city}, {setup.state} {setup.zip}
        </div>
      </Section>

      {/* Banking */}
      <Section icon={Landmark} title="Direct Deposit (masked)">
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
          <div><span className="text-zinc-500">Bank:</span> <span className="text-zinc-200">{setup.banking?.bank_name}</span></div>
          <div><span className="text-zinc-500">Type:</span> <span className="text-zinc-200 capitalize">{setup.banking?.account_type}</span></div>
          <div><span className="text-zinc-500">Routing:</span> <span className="text-zinc-200 font-mono">{setup.banking?.routing_number}</span></div>
          <div><span className="text-zinc-500">Account:</span> <span className="text-zinc-200 font-mono">{setup.banking?.account_number}</span></div>
        </div>
      </Section>

      {/* W-9 */}
      <Section icon={Sparkles} title="W-9 Tax Information (masked)">
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
          <div><span className="text-zinc-500">Legal:</span> <span className="text-zinc-200">{setup.w9?.legal_name}</span></div>
          {setup.w9?.business_name && (
            <div><span className="text-zinc-500">Business:</span> <span className="text-zinc-200">{setup.w9.business_name}</span></div>
          )}
          <div><span className="text-zinc-500">Classification:</span> <span className="text-zinc-200 capitalize">{(setup.w9?.tax_classification || "").replaceAll("_", " ")}</span></div>
          <div><span className="text-zinc-500">{setup.w9?.tin_type?.toUpperCase()}:</span> <span className="text-zinc-200 font-mono">{setup.w9?.tin_number}</span></div>
        </div>
      </Section>

      {/* References */}
      <Section icon={Users} title="Professional References (3)">
        <div className="space-y-1.5">
          {(setup.professional_references || []).map((r, i) => (
            <div key={i} className="text-xs border-l-2 border-zinc-700 pl-2">
              <div className="text-zinc-200 font-semibold">{r.name} <span className="text-zinc-500 font-normal">· {r.relationship}</span></div>
              <div className="text-zinc-500 text-[10px]">{r.phone} · {r.email}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Emergency contact */}
      <Section icon={Phone} title="Emergency Contact">
        <div className="text-xs text-zinc-200">
          {setup.emergency_contact?.name} <span className="text-zinc-500">· {setup.emergency_contact?.relationship}</span>
          <div className="text-zinc-500 text-[10px]">{setup.emergency_contact?.phone}</div>
        </div>
      </Section>

      {/* Availability */}
      <Section icon={Calendar} title="Availability">
        <div className="text-xs">
          <div className="flex flex-wrap gap-1 mb-1">
            {(setup.availability?.days_available || []).map((d) => (
              <span key={d} className="bg-amber-400/20 text-amber-300 border border-amber-500/40 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full">
                {d}
              </span>
            ))}
          </div>
          <div className="text-zinc-300">
            {setup.availability?.start_time} → {setup.availability?.end_time}
          </div>
        </div>
      </Section>

      {/* Signature PDF */}
      <Section icon={PenLine} title="Background Release · Signed PDF">
        {setup.signature_pdf_url ? (
          <FileActions
            url={`${bk}${setup.signature_pdf_url}`}
            filename={`signed-release-${setup.email || setup.id}.pdf`}
            testidPrefix="setup-signature-pdf"
            emphasize
          />
        ) : (
          <div className="text-xs text-rose-400 italic">Signature PDF missing.</div>
        )}
        <div className="text-[10px] text-zinc-500 mt-1">
          Agreed on {setup.background_release_date} · Submitted {fmtDate(setup.created_at)}
        </div>
      </Section>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">
        {Icon && <Icon className="h-3 w-3"/>}
        {title}
      </div>
      <div>{children}</div>
    </div>
  );
}

/**
 * FileActions · v2.7.65+E80
 * ══════════════════════════
 * Compact 3-button row (View · Download · Print) that works for any
 * `/api/uploads/{id}` URL. Because the API and the SPA live on the same
 * origin behind the ingress, the browser's built-in save/print behavior
 * fires without CORS gymnastics.
 *
 * Print flow: opens the file in a hidden iframe, waits for load, then
 * triggers `contentWindow.print()`. Works for PDFs and images.
 */
function FileActions({ url, filename, testidPrefix, emphasize = false }) {
  // Build the "download" flavor of the URL so browsers show a save-as
  // dialog instead of previewing in-tab.
  const downloadUrl = (() => {
    const u = new URL(url, window.location.origin);
    u.searchParams.set("disposition", "attachment");
    if (filename) u.searchParams.set("filename", filename);
    return u.toString();
  })();

  const doPrint = () => {
    try {
      // Clean up any previous stub.
      const prior = document.getElementById("app-print-frame");
      if (prior) prior.remove();
      const iframe = document.createElement("iframe");
      iframe.id = "app-print-frame";
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width  = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      iframe.src = url;
      iframe.onload = () => {
        // small delay so PDF plugin has time to paint
        setTimeout(() => {
          try { iframe.contentWindow?.focus(); iframe.contentWindow?.print(); }
          catch { window.open(url, "_blank"); }
        }, 400);
      };
      document.body.appendChild(iframe);
    } catch {
      window.open(url, "_blank");
    }
  };

  const bg = emphasize
    ? "bg-emerald-500/15 hover:bg-emerald-500/25 border-emerald-600/50 text-emerald-200"
    : "bg-zinc-900 hover:bg-zinc-800 border-zinc-700 hover:border-amber-400/60 text-zinc-100";

  return (
    <div className="space-y-1">
      <div className="text-[10px] text-zinc-500 truncate" title={filename}>{filename}</div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1.5 rounded border transition ${bg}`}
          data-testid={`${testidPrefix}-view`}
        >
          <Eye className="h-3 w-3"/> View
        </a>
        <a
          href={downloadUrl}
          className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1.5 rounded border transition ${bg}`}
          data-testid={`${testidPrefix}-download`}
        >
          <Download className="h-3 w-3"/> Download
        </a>
        <button
          type="button"
          onClick={doPrint}
          className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1.5 rounded border transition ${bg}`}
          data-testid={`${testidPrefix}-print`}
        >
          <Printer className="h-3 w-3"/> Print
        </button>
      </div>
    </div>
  );
}

